import { type FormEvent, useState } from 'react';
import { CheckoutSection } from '@/common.styled';
import { Checkbox } from '@/components/Checkbox';
import { Form } from '@/components/Form';
import { Heading } from '@/components/Heading';
import { HelpTip } from '@/components/HelpTip';
import { Link } from '@/components/Link';
import { TextField } from '@/components/TextField';
import { usePageData } from '@/contexts/pageContext';
import { useDataLayer } from '@/hooks/useDataLayer';
import { useShipping } from '@/contexts/shippingContext';
import { useUser } from '@/contexts/userContext';
import { useValidation } from '@/contexts/validationContext';
import * as Styled from './ContactSection.styled';
import { useCheckEmail } from './useCheckEmail';
import { ValidationMessage, ValidationSectionKey } from '@/constants';

export const ContactSection = () => {
  const { urls, helpText, consentMessage } = usePageData();
  const {
    orderPayload: { email, emailOptInNewsAndOffers },
    setEmail,
    setMarketingOptIn,
  } = useShipping();
  const {
    clearValidationError,
    errorMessages,
    setValidationError,
  } = useValidation();
  const { isAuthenticated } = useUser();
  const { formInteract } = useDataLayer();
  const [errors, setErrors] = useState({});
  const [hasWarning, setHasWarning] = useState(false);

  const emailValidationError = errorMessages[ValidationSectionKey.Email];

  const handleServerValidation = (errorObj: Record<string, string>) => {
    setErrors(errorObj);
    setHasWarning(Boolean(errorObj.email));
    if (errorObj.email) {
      formInteract('checkout', 'email', 'error');
    }
  };

  const { checkEmail } = useCheckEmail(handleServerValidation);
  const handleChange = (email: string) => {
    setEmail(email);

    // Validate email length
    if (email.length > 80) {
      setValidationError(ValidationSectionKey.Email, ValidationMessage.EmailTooLong);
      formInteract('checkout', 'email', 'error');
    } else {
      clearValidationError(ValidationSectionKey.Email);
    }
  };

  const handleBlur = () => {
    if (email) {
      checkEmail({ email });
      formInteract('checkout', 'email', 'complete');
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleBlur();
  };

  return (
    <CheckoutSection>
      <Styled.HeadingWrapper className="contact-section">
        <Heading level={2}>Contact</Heading>
        {isAuthenticated ? null : (
          <Styled.LinkWrapper>
            {urls.login ? (
              <Link href={urls.login}>Log in to your account</Link>
            ) : null}
            <HelpTip
              label="Help with logging in"
              tip={
                helpText?.loginHelpText ??
                'Login to use your saved addresses and collect Rewards Points'
              }
              variant="help"
            />
          </Styled.LinkWrapper>
        )}
      </Styled.HeadingWrapper>
      {isAuthenticated ? (
        <>
          <div>({email})</div>
          <a href={urls.logout}>Log out</a>
        </>
      ) : (
        <Form
          handleSubmit={handleSubmit}
          withLabels={true}
          validationErrors={emailValidationError ? errorMessages : errors}
        >
          <TextField
            className={
              emailValidationError ? 'has-validation-error' : undefined
            }
            hasWarning={hasWarning}
            label="Email"
            name={ValidationSectionKey.Email}
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={email}
          />
          <Checkbox
            isSelected={emailOptInNewsAndOffers}
            label={helpText?.signupLabel ?? 'Email me with news and offers.'}
            onChange={setMarketingOptIn}
          />
          <div
            // biome-ignore lint/security/noDangerouslySetInnerHtml: we trust the source of the HTML
            dangerouslySetInnerHTML={{ __html: consentMessage ?? '' }}
          />
        </Form>
      )}
    </CheckoutSection>
  );
};
